import {sellingPrice} from './../helper/index'
import reject from 'lodash/reject'
import omit from 'lodash/omit'
import moment from 'moment'
import apolloClient from 'apollo-client'
import {
  COMPLETE_DRAFT_ORDER,
  CREATE_DRAFT_ORDER,
  UPDATE_DRAFT_ORDER,
  DELETE_DRAFT_ORDER_ITEM,
  CREATE_ORDER,
  UPDATE_WINE_CLUB_ORDER,
  DELETE_DRAFT_ORDER_SHIPPING,
  UPDATE_ORDER,
} from 'app/features/orders/graphql'
import * as models from '../models'

const omitKeys = (order: any) => {
  return {
    ...omit(
      order,
      'wineClubDiscountCodeId',
      'discountCodeId',
      'isFreeShipping',
      'wineClubDiscounts',
      'totalDiscounts',
      'crv',
      'salesTax'
    ),
  }
}

export const createDraftOrder = async (draftOrder: models.DraftOrder) => {
  const order = mapToDraftOrder(draftOrder)
  const {data, errors} = await apolloClient.mutate({
    mutation: CREATE_DRAFT_ORDER,
    variables: {
      draftOrder: omitKeys(order),
    },
  })
  if (errors) {
    throw errors
  }
  const {id} = data.createDraftOrder.draftOrder
  return id
}

export const createOrder = async (
  draftOrder: models.DraftOrder,
  shippingAddressId: string,
  wineClubOrderId: string
) => {
  const model: models.Order = mapDraftOrderToOrder(draftOrder, shippingAddressId)

  const {data} = await apolloClient.mutate({
    mutation: CREATE_ORDER,
    variables: {
      order: model,
    },
  })
  const {
    createOrder: {order},
  } = data
  return await completedWineClubOrder(order, wineClubOrderId)
}

export const completedDraftOrder = async (
  draftOrder: models.DraftOrder,
  shippingAddressId: string,
  wineClubOrderId: string
) => {
  const model: models.Order = mapDraftOrderToOrder(draftOrder, shippingAddressId)
  const draftOrderPatch = mapToUpdateDraftOrder(draftOrder)

  const {data} = await apolloClient.mutate({
    mutation: COMPLETE_DRAFT_ORDER,
    variables: {
      order: model,
      draftOrder: draftOrderPatch,
    },
  })
  const {
    createOrder: {order},
  } = data
  return await completedWineClubOrder(order, wineClubOrderId)
}

export const updateDraftOrder = async (draftOrder: models.DraftOrder, id: any) => {
  const {
    items,
    deletedItems,
    shippingRate,
    isCompliant,
    shippingRateId,
    paymentMethodId,
    paymentType,
    ...order
  } = draftOrder
  const _draftOrder: any = {
    ...omitKeys(order),
    draftOrderItems: {
      create: filterNewDraftOrderItems(items),
      updateById: filterOldDraftOrderItems(items),
      deleteOthers: true,
    },
  }

  if (shippingRateId) {
    _draftOrder.shippingRateId = shippingRateId
  }
  if (paymentMethodId) {
    _draftOrder.paymentMethodId = paymentMethodId
  } else {
    _draftOrder.paymentMethodId = null
  }
  if (paymentType !== models.typesPayment.card) {
    _draftOrder.billingAddressId = null
  }
  return apolloClient.mutate({
    mutation: UPDATE_DRAFT_ORDER,
    variables: {
      draftOrder: _draftOrder,
      id,
    },
  })
}

export const deleteDraftOrderItem = async (draftOrderItemId: string) => {
  return apolloClient.mutate({
    mutation: DELETE_DRAFT_ORDER_ITEM,
    variables: {draftOrderItemId},
  })
}

export const deleteDraftOrderShipping = async ({id, shippingRateId}: any) => {
  return apolloClient.mutate({
    mutation: DELETE_DRAFT_ORDER_SHIPPING,
    variables: {draftOrder: {shippingRateId, draftOrderId: id}},
  })
}

const completedWineClubOrder = async ({id}: any, wineClubOrderId: string) => {
  if (wineClubOrderId) {
    const wineClubOrder = {
      orderId: id,
      status: 'COMPLETED',
    }
    return updateWineClubOrder(wineClubOrder, wineClubOrderId)
  }
}

const updateWineClubOrder = async (order: any, id: string) => {
  const wineClubOrder = {
    patch: order,
    id,
  }
  return apolloClient.mutate({
    mutation: UPDATE_WINE_CLUB_ORDER,
    variables: {wineClubOrder},
  })
}

const mapDraftOrderToOrder = (draftOrder: models.DraftOrder, shippingAddressId: string) => {
  const orderItems: models.OrderItem[] = draftOrder.items.map((lineItem) => ({
    productId: lineItem.productId,
    variantId: lineItem.variantId,
    quantity: lineItem.quantity,
    price: lineItem.price,
    requiresShipping: true,
    totalDiscount: 0,
  }))

  const order: models.Order = {
    name: '',
    customerId: draftOrder.customerId,
    shippingAddressId: shippingAddressId,
    billingAddressId: shippingAddressId,
    totalDiscounts: draftOrder.totalDiscounts,
    totalPrice: draftOrder.totalPrice,
    totalTax: draftOrder.totalTax,
    totalLineItemPrice: draftOrder.totalPrice,
    subtotalPrice: draftOrder.subtotalPrice,
    currentSubtotalPrice: draftOrder.subtotalPrice,
    currentTotalDiscounts: draftOrder.totalDiscounts,
    currentTotalPrice: draftOrder.totalPrice,
    currentTotalTax: draftOrder.totalTax,
    financialStatus: 'PAID',
    taxesIncluded: false,
    totalWeight: 0,
    totalOutstanding: 0,
    orderItems: {create: orderItems},
    hasWineClub: draftOrder.hasWineClub,
    orderSubtype: draftOrder.orderSubtype,
  }

  if (!draftOrder.id) {
    const _draftOrder = mapToDraftOrder(draftOrder)
    _draftOrder.status = 'COMPLETED'
    order.draftOrders = {create: _draftOrder}
  }

  return order
}

const mapToDraftOrder = (draftOrder: models.DraftOrder) => {
  const {
    items,
    deletedItems,
    isFreeShipping,
    isCompliant,
    shippingRateId,
    paymentMethodId,
    paymentType,
    ...order
  } = draftOrder
  const draft: any = order
  if (shippingRateId) {
    draft.shippingRateId = shippingRateId
  }

  if (paymentMethodId) {
    draft.paymentMethodId = paymentMethodId
  } else {
    draft.paymentMethodId = null
  }

  if (paymentType !== models.typesPayment.card) {
    draft.billingAddressId = null
  }

  const draftOrderItems = items.map((item) => ({
    productId: item.productId,
    quantity: item.quantity,
    variantId: item.variantId,
    price: sellingPrice(item),
  }))
  return {...draft, draftOrderItems: {create: draftOrderItems}}
}

const mapToUpdateDraftOrder = (draftOrder: models.DraftOrder) => {
  return {
    id: draftOrder.id,
    patch: {
      status: 'COMPLETED',
      completedAt: moment().utc(),
    },
  }
}

const filterNewDraftOrderItems = (items: models.DraftOrderItem[]) => {
  return reject(items, (item) => item.id).map((item: any) => ({
    productId: item.productId,
    quantity: item.quantity,
    variantId: item.variantId,
    price: sellingPrice(item),
  }))
}

const filterOldDraftOrderItems = (items: models.DraftOrderItem[]) => {
  return reject(items, (item) => !item.id).map((item: any) => ({
    patch: {
      quantity: item.quantity,
      price: sellingPrice(item),
    },
    id: item.id,
  }))
}


export const updateDraftOrderSubtypeId = async (draftOrderId: string, orderSubtypeId: string | null) => {
  const patch = {
    orderSubtypeId: orderSubtypeId
  }

  return apolloClient.mutate({
    mutation: UPDATE_DRAFT_ORDER,
    variables: {
      draftOrder: patch,
      id: draftOrderId,
    },
  })
}


export const updateOrderSubtypeId = async (orderId: string, orderSubtypeId: string | null) => {
  const patch = {
    orderSubtypeId: orderSubtypeId
  }

  return apolloClient.mutate({
    mutation: UPDATE_ORDER,
    variables: {
      patch,
      id: orderId,
    },
  })
}
